import { AbstractControl, ValidationErrors } from '@angular/forms';
import { realmRoles, UserRole } from '@app/features/users/user.model';
import { Moment } from 'moment';
import { isAfterEnd } from './paris-time';

/**
 * Ce qu'une anomalie et une annonce saisissent de la même façon : les rôles cibles
 * (RG-anomalie-ciblage-par-role) et la période d'affichage, à l'heure de Paris
 * (RG-anomalie-periode-d-affichage). L'annonce reprend ces règles sans les modifier (décision
 * 2026-032/06).
 */

/** Entrée « Tous les rôles » de la liste des rôles cibles : elle coche tous les rôles de CDT. */
export const ALL_ROLES = '*';
export type RoleOption = UserRole | typeof ALL_ROLES;

/** Les rôles retenus, dans l'ordre de CDT, sans l'entrée « Tous les rôles ». */
export function realRoles(options: RoleOption[] | null): UserRole[] {
  return realmRoles.filter((role) => options?.includes(role));
}

export function atLeastOneRole(control: AbstractControl<RoleOption[]>): ValidationErrors | null {
  return realRoles(control.value).length ? null : { required: true };
}

/** Les rôles, précédés de « Tous les rôles » quand ils y sont tous. */
export function withAllRoles(roles: UserRole[]): RoleOption[] {
  const ordered = realRoles(roles);
  return ordered.length === realmRoles.length ? [ALL_ROLES, ...ordered] : ordered;
}

/** « Tous les rôles » coche ou décoche tout ; retirer un rôle le décoche. */
export function nextRoleSelection(previous: RoleOption[], selected: RoleOption[]): RoleOption[] {
  const hadAll = previous.includes(ALL_ROLES);
  const hasAll = selected.includes(ALL_ROLES);
  let roles = realRoles(selected);
  if (hasAll && !hadAll) {
    roles = [...realmRoles];
  } else if (!hasAll && hadAll && roles.length === realmRoles.length) {
    roles = [];
  }
  return hasAll && hadAll && roles.length < realmRoles.length ? roles : withAllRoles(roles);
}

export interface PeriodValue {
  startDate: Moment | null;
  startTime: string | null;
  endDate: Moment | null;
  endTime: string | null;
}

export interface Bounds {
  /** `YYYY-MM-DD` */
  startDate: string | null;
  /** `HH:mm` */
  startTime: string | null;
  endDate: string | null;
  endTime: string | null;
}

/** Les bornes saisies, telles que l'API les attend : date et heure de Paris, chacune facultative. */
export function bounds(value: PeriodValue): Bounds {
  const date = (d: Moment | null) => (d?.isValid() ? d.format('YYYY-MM-DD') : null);
  const time = (t: string | null) => (t ? t.slice(0, 5) : null);
  return {
    startDate: date(value.startDate),
    startTime: time(value.startTime),
    endDate: date(value.endDate),
    endTime: time(value.endTime),
  };
}

/** Une heure se saisit avec sa date, et la fin suit le début. */
export function validPeriod(group: AbstractControl): ValidationErrors | null {
  const value = bounds(group.getRawValue() as PeriodValue);
  if ((value.startTime && !value.startDate) || (value.endTime && !value.endDate)) {
    return { timeWithoutDate: true };
  }
  if (
    value.startDate &&
    value.endDate &&
    isAfterEnd(
      { date: value.startDate, time: value.startTime },
      { date: value.endDate, time: value.endTime },
    )
  ) {
    return { endBeforeStart: true };
  }
  return null;
}
